import {TrackingEventDefinition} from "./tracking.js";

export class TrackingEventDefinitionReader {
    private readonly onAdded: (definition: TrackingEventDefinition) => void;
    private readonly mutationObserver: MutationObserver;
    private readonly knownSources = new WeakSet<HTMLElement>();
    private started = false;

    constructor(onAdded: (definition: TrackingEventDefinition) => void) {
        this.onAdded = onAdded;
        this.mutationObserver = new MutationObserver(this.observeCallback.bind(this));
    }

    public start() {
        if (this.started) {
            return;
        }
        this.started = true;

        this.readFromCurrentDocument();

        this.mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    private readFromCurrentDocument() {
        const elements = document.querySelectorAll('script[type="application/json"][data-clxgtm-tracking-definition]');
        for (const element of elements) {
            this.tryReadFromNode(element);
        }
    }

    private observeCallback(mutations: MutationRecord[]) {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                this.tryReadFromNode(node);
            }
        }
    }

    private tryReadFromNode(node: Node) {
        if (! (node instanceof HTMLElement)) {
            return;
        }
        if (this.knownSources.has(node)) {
            return;
        }

        const definition = tryExtractTrackingEventDefinition(node);
        if (definition) {
            this.knownSources.add(node);
            this.onAdded(definition);
        }
    }
}

function tryExtractTrackingEventDefinition(node: Node): TrackingEventDefinition|undefined {
    if (! (node instanceof HTMLScriptElement)) {
        return;
    }
    if (node.type !== 'application/json') {
        return;
    }
    if (! node.hasAttribute('data-clxgtm-tracking-definition')) {
        return;
    }

    const json = node.textContent;
    if (! json) {
        return;
    }

    let definition;
    try {
        definition = JSON.parse(json);
    } catch {
        return;
    }

    return definition as TrackingEventDefinition;
}
